/**
 * Design tokens v2 (Ink) — SSOT for BeClients 2.0 redesign.
 * Spec: DOC/beclients-2.0/redesign/TOKENS-v2.md + IMPLEMENTATION-1-1.md
 * Runtime: applyDesignConfig → var(--v2-*)
 * Legacy ui-tokens / design-config re-export v2 where values overlap.
 */

const STATUS_PILL_BG_PCT = 12;
const STATUS_PILL_TEXT_COLOR_PCT = 60;
const STATUS_PILL_TEXT_BLACK_PCT = 40;

const ELEVATION_FLOAT =
  '0 1px 2px rgba(16,20,28,.06), 0 10px 24px -10px rgba(16,20,28,.35)';
const ELEVATION_SHEET =
  '0 1px 2px rgba(16,20,28,.06), 0 -16px 40px -16px rgba(16,20,28,.35)';
const ELEVATION_APPOINTMENT_CARD =
  '0 2px 6px rgba(16,20,28,.14), 0 8px 18px rgba(16,20,28,.08)';
const ELEVATION_APPOINTMENT_CARD_OVERLAP = ELEVATION_APPOINTMENT_CARD;

export const v2Spacing = {
  4: '4px',
  6: '6px',
  8: '8px',
  10: '10px',
  12: '12px',
  14: '14px',
  16: '16px',
  20: '20px',
  24: '24px',
} as const;

/** Inter scale from TOKENS-v2.md */
export const v2Typography = {
  fontSize: {
    screenTitle: '24px',
    sheetTitle: '20px',
    dialogTitle: '18px',
    cta: '16px',
    listName: '15px',
    body: '14px',
    meta: '13px',
    fieldLabel: '12px',
    micro: '11px',
  },
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
  },
  letterSpacing: {
    screenTitle: '-0.02em',
    sheetTitle: '-0.01em',
    fieldLabel: '0.03em',
    none: '0',
  },
  lineHeight: {
    tight: '1',
    snug: '1.2',
    normal: '1.4',
  },
  fontFamily:
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  fontVariantNumericTabular: 'tabular-nums',
} as const;

export const v2Tokens = {
  spacing: v2Spacing,

  typography: v2Typography,

  colors: {
    accent: '#FFC107',
    onAccent: '#1A1C1F',
    ink: '#4B4137',
    textSecondary: '#5C636E',
    textMuted: '#8A9099',
    iconMuted: '#C6CAD1',
    border: '#E8EAEE',
    borderFrame: '#D5D9E0',
    canvas: '#F6F7F9',
    surface: '#FFFFFF',
    warmTint: '#FFF3CD',
    link: '#8A6500',
    status: {
      // Cleaner, higher-chroma schedule palette. Inspired by the BerryMore reference:
      // cyan #45BFD3, pink #F06292, violet #9B6BFF.
      done: '#45A876',
      pending: '#45BFD3',
      neutral: '#98A2B3',
      cancelled: '#F06292',
      noShow: '#E7A33E',
      moved: '#9B6BFF',
    },
  },

  radius: {
    sm: '8px',
    md: '14px',
    sheet: '20px',
    frame: '22px',
    pill: '99px',
  },

  elevation: {
    float: ELEVATION_FLOAT,
    sheet: ELEVATION_SHEET,
    appointmentCard: ELEVATION_APPOINTMENT_CARD,
    appointmentCardOverlap: ELEVATION_APPOINTMENT_CARD_OVERLAP,
  },

  layout: {
    statusStripeWidth: '3px',
    tabBarHeight: '64px',
    tabBarIconSize: '22px',
    tabBarIndicatorWidth: '20px',
    tabBarIndicatorHeight: '2px',
    agentButtonSize: '64px',
    agentButtonOffset: '-8px',
    agentIconSize: '26px',
    fabSize: '52px',
    fabIconSize: '24px',
    fabRight: '16px',
    fabBottom: '80px',
    appointmentCardRadius: '8px',
    appointmentCardPaddingX: '14px',
    appointmentCardPaddingY: '12px',
    /** Критический минимум padding карточки (таймлайн / узкие слоты). Не уменьшать ниже. */
    appointmentCardPaddingMinX: '8px',
    appointmentCardPaddingMinY: '4px',
    /** Средний padding между default и min для компактных карточек таймлайна. */
    appointmentCardPaddingCompactX: '10px',
    appointmentCardPaddingCompactY: '6px',
    appointmentCardHeaderGap: v2Spacing[4],
    appointmentCardSummaryGap: v2Spacing[8],
    /** Высота блока таймлайна (px): ≤ compact → compact padding; ≤ minimal → min padding. */
    appointmentCardTimelineCompactMaxHeight: 88,
    appointmentCardTimelineMinimalMaxHeight: 56,
    appointmentCardTimelineCompactHeaderGap: v2Spacing[4],
    appointmentCardTimelineCompactSummaryGap: v2Spacing[4],
    appointmentCardTimelineMinimalHeaderGap: '2px',
    appointmentCardTimelineMinimalSummaryGap: '2px',
    scheduleFeedTimeColWidth: '44px',
    /** Вертикальный/горизонтальный gutter дня: над сводкой и над лентой/таймлайном */
    scheduleDayGutterY: v2Spacing[8],
    scheduleDayGutterX: v2Spacing[16],
    /** Padding внутри строки сводки (8px сверху и снизу) */
    scheduleSummaryPaddingY: v2Spacing[8],
    scheduleSummaryPaddingX: v2Spacing[16],
    /** Вертикальный gap между строками ленты расписания */
    scheduleFeedRowPaddingY: v2Spacing[10],
    freeSlotMinHeight: '44px',
    listRowMinHeight: '56px',
    listRowMinHeightMax: '64px',
    listRowAvatarSize: '40px',
    listRowPaddingX: v2Spacing[16],
    listRowPaddingY: v2Spacing[12],
    clientDetailHistoryEmptyMinHeight: '200px',
    clientDetailHistoryTimeMinWidth: '80px',
    inputHeight: '48px',
    inputHeightMax: '52px',
    sheetGrabberWidth: '36px',
    sheetGrabberHeight: '4px',
    tapTargetMin: '44px',
    screenPadding: v2Spacing[16],
    formFieldGap: v2Spacing[20],
  },

  statusPill: {
    bgMixPct: STATUS_PILL_BG_PCT,
    textColorPct: STATUS_PILL_TEXT_COLOR_PCT,
    textBlackPct: STATUS_PILL_TEXT_BLACK_PCT,
  },
} as const;

/** Pill background: status color @ 12% on surface */
export function v2StatusPillBg(baseColor: string): string {
  return `color-mix(in srgb, ${baseColor} ${STATUS_PILL_BG_PCT}%, ${v2Tokens.colors.surface} ${100 - STATUS_PILL_BG_PCT}%)`;
}

/** Pill text: status color ~40% darker */
export function v2StatusPillText(baseColor: string): string {
  return `color-mix(in srgb, ${baseColor} ${STATUS_PILL_TEXT_COLOR_PCT}%, #000000 ${STATUS_PILL_TEXT_BLACK_PCT}%)`;
}
